import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import type { FormFields } from '@atlaskit/feedback-collector';
import { ModalTransition } from '@atlaskit/modal-dialog';
import { useFlagService } from '@atlassian/jira-flags';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { useNativeLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { useGlobalRef } from '@atlassian/jira-refs-store/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { EntryKeys } from '../../constants.tsx';
import { useFeedbackCollector } from '../../controllers/index.tsx';
import { fireFeedbackCreatedAnalytics } from '../../services/analytics/index.tsx';
import { FEEDBACK_CONTEXT_CUSTOM_FIELD, LINKED_ISSUES_BUTTON } from './constants.tsx';
import { LazyFeedbackCollectorDialog } from './dialog/async.tsx';
import { successFlagConfigWithoutAppify, successFlagConfig } from './success-flag/index.tsx';
import { createAnalyticsPayload } from './utils.tsx';
/**
 * This component serves as the interface for collecting user feedback within the application.
 * It leverages various hooks to manage locale settings and user context, ensuring a tailored
 * feedback collection experience. The component dynamically displays a feedback dialog when
 * conditions are met, facilitating the submission and processing of user feedback. It
 * incorporates analytics to track feedback submissions and employs a notification system to
 * confirm successful submissions to the user. This approach not only enriches the user
 * experience by making feedback submission intuitive but also aids in gathering valuable
 * insights from users efficiently.
 */
const FeedbackRenderer = () => {
	const locale = useNativeLocale();
	const [
		{
			isFeedbackCollectorOpen,
			feedbackCollectorId,
			additionalFields,
			feedbackContext,
			feedbackGroupId,
			onClose,
			onSubmit,
			entrypointId,
			renderCustomContent,
			...feedbackCollectorProps
		},
		{ closeFeedbackCollector, resetFeedbackCollectorState },
	] = useFeedbackCollector();
	const { userFullname, atlassianAccountId, isAnonymous } = useTenantContext();
	const { showFlag } = useFlagService();
	const [feedbackTriggerRef] = useGlobalRef(LINKED_ISSUES_BUTTON);
	const feedbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(
		() => () => {
			if (feedbackTimeoutRef.current) clearTimeout(feedbackTimeoutRef.current);
		},
		[],
	);

	const additionalFieldsWithContext = useMemo(() => {
		const fields = additionalFields?.slice() ?? [];
		let maybeFeedbackContext = feedbackContext;
		if (feedbackGroupId != null) {
			maybeFeedbackContext = maybeFeedbackContext || [];
			maybeFeedbackContext.push({
				id: EntryKeys.FEEDBACK_GROUP_ID,
				value: feedbackGroupId,
			});
		}
		if (maybeFeedbackContext != null) {
			fields.push({
				id: FEEDBACK_CONTEXT_CUSTOM_FIELD,
				value: maybeFeedbackContext.map(({ id, value }) => `${id}: ${String(value)}`).join('\n'),
			});
		}
		return fields;
	}, [additionalFields, feedbackContext, feedbackGroupId]);
	const onFeedbackSubmit = useCallback(
		(feedbackDetails: FormFields) => {
			const analyticsPayload = createAnalyticsPayload(feedbackCollectorId, feedbackDetails);
			fireFeedbackCreatedAnalytics(entrypointId, analyticsPayload);
			fg('product-terminology-refresh')
				? showFlag(successFlagConfig)
				: showFlag(successFlagConfigWithoutAppify);
			onSubmit?.();
			resetFeedbackCollectorState();
		},
		[onSubmit, entrypointId, feedbackCollectorId, resetFeedbackCollectorState, showFlag],
	);
	const onFeedbackClose = useCallback(() => {
		closeFeedbackCollector();
		feedbackTimeoutRef.current = setTimeout(() => {
			feedbackTriggerRef?.focus();
		}, 0);
		onClose?.();
	}, [closeFeedbackCollector, onClose, feedbackTriggerRef]);
	const shouldRender = isFeedbackCollectorOpen && !!entrypointId;

	// Description needs to be defined in the FeedbackCollector in order for the form to be submittable
	// but because the FeedbackCollector component manages the state of the description field internally, we can't
	// directly modify it. Instead, setting descriptionDefaultValue works around this issue and allows us to
	// set the description and make the form submittable.
	// It's a wacky workaround, but it appears to be used in other places too.
	const [description, setDescription] = useState<string>('');
	return (
		<ModalTransition>
			<Placeholder name="async-feedback-collector-dialog" fallback={null}>
				{shouldRender ? (
					// @ts-expect-error - TS2769 - No overload matches this call.
					<LazyFeedbackCollectorDialog
						locale={locale}
						entrypointId={entrypointId}
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						atlassianAccountId={atlassianAccountId!}
						anonymousFeedback={isAnonymous}
						onClose={onFeedbackClose}
						onSubmit={onFeedbackSubmit}
						additionalFields={additionalFieldsWithContext}
						name={userFullname ?? undefined}
						customContent={renderCustomContent?.({
							setDescription,
						})}
						descriptionDefaultValue={description}
						summaryDefaultValue={description} // This is needed due to a bug in the component
						{...feedbackCollectorProps}
					/>
				) : null}
			</Placeholder>
		</ModalTransition>
	);
};
export default FeedbackRenderer;
